import { useEffect, useState, type FormEvent } from 'react';
import { supabase } from '../lib/supabase';
import PageHeader from '../components/PageHeader';
import { useConfirm } from '../components/ConfirmDialog';

// أخبار الصفحة الرئيسية — home_updates. صور اختيارية، عناوين
// عربي+إنجليزي، تاريخ نشر، وحالة ظاهر/مخفي.

type HomeUpdate = {
  id: string;
  title_ar: string;
  title_en: string;
  body_ar: string;
  body_en: string;
  image_url: string | null;
  active: boolean;
  published_at: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

type Draft = Omit<HomeUpdate, 'created_at' | 'updated_at' | 'id'> & { id?: string };

const emptyDraft: Draft = {
  title_ar: '',
  title_en: '',
  body_ar: '',
  body_en: '',
  // The form no longer exposes image / date / sort / active — every
  // published news item is active, published now, and ordered by
  // created_at desc. Keep the columns on the row shape so we can still
  // read whatever prod already stored.
  image_url: null,
  active: true,
  published_at: new Date().toISOString(),
  sort_order: 0,
};

export default function HomeNewsPage() {
  const [rows, setRows] = useState<HomeUpdate[]>([]);
  const [editing, setEditing] = useState<Draft | null>(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const confirm = useConfirm();

  useEffect(() => { void load(); }, []);

  const load = async () => {
    setLoading(true);
    // Newest on top. published_at is auto-set to insert time now, so
    // ordering by it matches "الأحدث فوق" for both admin-created and
    // any legacy rows.
    const { data, error: err } = await supabase.from('home_updates')
      .select('*').order('published_at', { ascending: false });
    if (err) setError(err.message);
    else setRows((data ?? []) as HomeUpdate[]);
    setLoading(false);
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    // Force sensible defaults on save: always active, published now on
    // create so newest sorts to top, sort_order zeroed since it's no
    // longer exposed.
    const payload = {
      ...editing,
      active: true,
      sort_order: 0,
      updated_at: new Date().toISOString(),
      ...(creating ? { published_at: new Date().toISOString() } : {}),
    };
    const { error: err } = creating
      ? await supabase.from('home_updates').insert(payload)
      : await supabase.from('home_updates').update(payload).eq('id', editing.id!);
    if (err) return setError(err.message);
    setEditing(null); setCreating(false); await load();
  };

  const remove = async (id: string) => {
    if (!(await confirm({
      title: 'حذف الخبر ده؟',
      confirmText: 'احذف',
      tone: 'danger',
    }))) return;
    const { error: err } = await supabase.from('home_updates').delete().eq('id', id);
    if (err) setError(err.message); else await load();
  };

  return (
    <>
      <PageHeader
        eyebrow="الرئيسية"
        title="آخر الأخبار"
        subtitle="الأخبار اللي بتظهر تحت الكاروسيل ف الصفحة الرئيسية للتطبيق"
        actions={<button className="btn" onClick={() => { setCreating(true); setEditing({ ...emptyDraft }); }}>+ خبر جديد</button>}
      />
      {error && <div className="error-inline">{error}</div>}
      {loading ? (
        <div className="empty">جاري التحميل...</div>
      ) : rows.length === 0 ? (
        <div className="card empty">مفيش أخبار — ابدأ بإضافة خبر.</div>
      ) : (
        <table className="table">
          <thead>
            <tr><th>العنوان</th><th>تاريخ النشر</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>
                  <div>{r.title_ar}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{r.title_en}</div>
                </td>
                <td>{new Date(r.published_at).toLocaleDateString('ar-EG', { timeZone: 'Africa/Cairo' })}</td>
                <td style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-small btn-secondary" onClick={() => { setCreating(false); setEditing({ ...r }); }}>تعديل</button>
                  <button className="btn btn-small btn-danger" onClick={() => void remove(r.id)}>حذف</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <div className="modal-backdrop" onClick={() => setEditing(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{creating ? 'خبر جديد' : `تعديل: ${editing.title_ar}`}</h2>
              <button className="btn btn-small btn-secondary" onClick={() => setEditing(null)}>إغلاق</button>
            </div>
            <form onSubmit={save}>
              <div className="row">
                <div className="field"><label>العنوان (عربي)</label><input required value={editing.title_ar} onChange={(e) => setEditing({ ...editing, title_ar: e.target.value })} /></div>
                <div className="field"><label>Title (English)</label><input value={editing.title_en} onChange={(e) => setEditing({ ...editing, title_en: e.target.value })} /></div>
              </div>
              <div className="row">
                <div className="field"><label>المحتوى (عربي)</label><textarea rows={4} value={editing.body_ar} onChange={(e) => setEditing({ ...editing, body_ar: e.target.value })} /></div>
                <div className="field"><label>Body (English)</label><textarea rows={4} value={editing.body_en} onChange={(e) => setEditing({ ...editing, body_en: e.target.value })} /></div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setEditing(null)}>إلغاء</button>
                <button type="submit" className="btn">{creating ? 'نشر' : 'حفظ'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
